import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Circle, GoogleMap, InfoWindow, Marker, useJsApiLoader, } from '@react-google-maps/api';
import React, { useContext } from 'react';
import { renderToString } from 'react-dom/server';
import { ThemeContext } from 'styled-components';
import { pxToRems, universalBtoa } from '@awaze/design-system.utils.react';
import { HouseIcon, KeyIcon } from './icons';
import { FailedMapPlaceholder, LoadingMapPlaceholder } from './map-placeholder';
/**
 * This is a map component that implements a Google map.
 *
 * There are several props that allow you to configure the maps display and features.
 *
 * This component is not unit tested. It's currently not possible to unit test this implementation, please see Github issue here: https://github.com/JustFly1984/react-google-maps-api/issues/113
 *
 * Testing is covered by the use of Chromatic snapshots to verify the visual output.
 */
export const Map = ({ apiKey, mapId = 'google-map-script', markers, zoomControl, defaultZoom = 7, maxZoom = 16, aspectRatio = '16:9', hideFeatures = [], translations, language, region, height, showMarkerRadius = true, icon = 'house', }) => {
    const themeContext = useContext(ThemeContext);
    const aspectRatioArray = aspectRatio
        .split(':')
        .map((val) => parseInt(val, 10));
    const mapHeight = height || `${(aspectRatioArray[1] / aspectRatioArray[0]) * 100}%`;
    const { isLoaded, loadError } = useJsApiLoader({
        id: mapId,
        googleMapsApiKey: apiKey,
        ...(region && { region }),
        ...(language && { language }),
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [map, setMap] = React.useState(null);
    const onLoad = React.useCallback(function callback(m) {
        const bounds = new window.google.maps.LatLngBounds();
        for (let i = 0; i < markers.length; i += 1) {
            bounds.extend(markers[i]);
        }
        m.fitBounds(bounds);
        setMap(m);
    }, []);
    const onUnmount = React.useCallback(function callback() {
        setMap(null);
    }, []);
    const containerStyle = {
        width: '100%',
        height: 0,
        paddingTop: mapHeight,
        borderRadius: pxToRems(8),
    };
    const radiusSize = 150;
    const radiusOptions = {
        clickable: false,
        draggable: false,
        editable: false,
        fillColor: themeContext.ColorThemeLink,
        fillOpacity: 0.28,
        strokeWeight: 0,
        visible: showMarkerRadius,
        zIndex: 1,
    };
    const stylesArray = hideFeatures.map((feature) => ({
        featureType: feature,
        stylers: [{ visibility: 'off' }],
    }));
    const houseIconString = renderToString(HouseIcon(themeContext));
    const keyIconString = renderToString(KeyIcon(themeContext));
    const houseIconSVG = universalBtoa(houseIconString);
    const keyIconSVG = universalBtoa(keyIconString);
    const markerUrl = `data:image/svg+xml;base64,${icon === 'house' ? houseIconSVG : keyIconSVG}`;
    if (loadError) {
        return (_jsx(FailedMapPlaceholder, { containerStyle: containerStyle, errorLabels: {
                heading: translations.failedMapPlaceholderHeading,
                text: translations.failedMapPlaceholderText,
            } }));
    }
    return isLoaded ? (_jsx(GoogleMap, { mapContainerStyle: containerStyle, zoom: defaultZoom, onLoad: onLoad, onUnmount: onUnmount, options: {
            maxZoom,
            mapTypeControl: false,
            streetViewControl: false,
            styles: stylesArray,
            zoomControl,
        }, children: _jsx(_Fragment, { children: markers &&
                markers.map((marker) => {
                    const { infoWindow } = marker;
                    return (_jsxs(Marker, { position: marker, icon: {
                            url: markerUrl,
                            anchor: new google.maps.Point(24, 22),
                            scaledSize: new google.maps.Size(48, 48),
                        }, children: [_jsx(Circle, { center: marker, radius: radiusSize, options: radiusOptions }), infoWindow && (_jsx(InfoWindow, { children: _jsx(_Fragment, { children: infoWindow.children }) }))] }, `marker_${marker.lat}|${marker.lng}`));
                }) }) })) : (_jsx(LoadingMapPlaceholder, { containerStyle: containerStyle }));
};
//# sourceMappingURL=map.js.map